import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

function OrderEdit({ order, onSave, onCancel, onDelete, theme, user }) {
    const { t } = useLanguage();
    const [form, setForm] = useState({
        name: order?.name || '',
        table_id: order?.table_id || '',
        closed: order?.closed || 0,
        // Add other order fields as needed
    });

    const [tables, setTables] = useState([]);

    // Check if user can delete orders (testdev user or owner role)
    const canDeleteOrders = user?.user_name === 'testdev' || user?.role_title?.toLowerCase() === 'owner';

    useEffect(() => {
        async function fetchTables() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/tables`);
                if (response.ok) {
                    const tablesData = await response.json();
                    setTables(tablesData);
                }
            } catch (error) {
                console.error(t('errorFetchingTables') + ':', error);
            }
        }
        fetchTables();
    }, [t]);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    }

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            const res = await fetch(`${BACKEND_URL}/api/orders/${order.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...order, ...form })
            });
            if (!res.ok) throw new Error(t('failedToUpdateOrder'));
            setSuccess(true);
            if (onSave) onSave({ ...order, ...form });
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    async function handleDeleteOrder() {
        if (!window.confirm(t('deleteOrderConfirmation').replace('this order', `"${order.name}"`))) {
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${order.id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || t('failedToDeleteOrder') || 'Failed to delete order');
            }

            // Success - notify parent component
            if (onDelete) onDelete(order.id);
        } catch (err) {
            setError(err.message || 'Failed to delete order');
            setSaving(false);
        }
    }

    return (
        <div style={{
            background: theme?.colors?.surface || '#fff',
            color: theme?.colors?.text || '#222',
            borderRadius: theme?.borderRadius || 8,
            boxShadow: theme?.boxShadow || '0 2px 8px #0002',
            padding: '1rem',
            maxWidth: 400,
            margin: '2rem auto',
        }}>
            <h2>{t('editOrder')}</h2>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                    <label>{t('orderName')}:</label>
                    <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        style={{ width: '100%' }}
                        required
                        disabled={saving}
                    />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                    <label>{t('table')}:</label>
                    <select
                        name="table_id"
                        value={form.table_id}
                        onChange={handleChange}
                        style={{ width: '100%' }}
                        required
                        disabled={saving}
                    >
                        <option value="">{t('selectTable')}</option>
                        {tables
                            .sort((a, b) => {
                                const numA = parseInt(a.name);
                                const numB = parseInt(b.name);
                                const aStartsWithNum = !isNaN(numA);
                                const bStartsWithNum = !isNaN(numB);
                                if (aStartsWithNum && bStartsWithNum) {
                                    return numA - numB;
                                } else if (aStartsWithNum) {
                                    return -1;
                                } else if (bStartsWithNum) {
                                    return 1;
                                } else {
                                    return a.name.localeCompare(b.name);
                                }
                            })
                            .map(table => (
                                <option key={table.id} value={table.id}>
                                    {table.name}
                                </option>
                            ))}
                    </select>
                </div>
                {/* Add more fields as needed */}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button type="submit" style={{ background: theme?.colors?.primary || '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '0.5rem 1rem' }} disabled={saving}>{t('save')}</button>
                    <button type="button" onClick={onCancel} style={{ background: theme?.colors?.secondary || '#aaa', color: '#fff', border: 'none', borderRadius: 4, padding: '0.5rem 1rem' }} disabled={saving}>{t('cancel')}</button>
                    {canDeleteOrders && (
                        <button
                            type="button"
                            onClick={handleDeleteOrder}
                            style={{
                                background: theme?.colors?.error || '#d32f2f',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                padding: '0.5rem 1rem',
                                cursor: 'pointer'
                            }}
                            disabled={saving}
                        >
                            {t('deleteOrder')}
                        </button>
                    )}
                </div>
                {error && <div style={{ color: 'red', marginTop: '1rem' }}>{t('error')}: {error}</div>}
                {success && <div style={{ color: 'green', marginTop: '1rem' }}>{t('orderUpdated')}!</div>}
            </form>
        </div>
    );
}

export default OrderEdit;
